import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Flag, UserX } from "lucide-react";
import MemberDialog from "@/components/admin/MemberDialog";

export default function ChatMonitor({ outingId, title, onClose }) {
    const qc = useQueryClient();
    const [q, setQ] = useState("");
    const [profileId, setProfileId] = useState(null);
    const { data } = useQuery({ queryKey: ["admin-chat", outingId], queryFn: async () => (await api.get(`/admin/outings/${outingId}/chat`)).data, enabled: !!outingId, refetchInterval: 5000 });
    const flag = async (mid, flagged) => {
        try { await api.post(`/admin/messages/${mid}/flag`, { flagged }); qc.invalidateQueries({ queryKey: ["admin-chat", outingId] }); }
        catch (e) { toast.error("Could not update flag."); }
    };
    const blockUser = async (uid, name) => {
        if (!uid) return;
        try {
            await api.put(`/admin/members/${uid}/status`, { verification_status: "blocked", account_status: "suspended" });
            toast.success(`${name || "Member"} blocked.`);
            qc.invalidateQueries();
        } catch (e) { toast.error("Could not block member."); }
    };
    const msgs = (data?.messages || []).filter((m) => !q || (m.text || "").toLowerCase().includes(q.toLowerCase()));
    return (
        <>
        <Dialog open={!!outingId} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle data-testid="chat-monitor-title">Live monitor: {title}</DialogTitle><DialogDescription>Read-only view of the group's chat with temporary-identity mapping.</DialogDescription></DialogHeader>
                {!data ? <p className="text-sm text-zinc-500">Loading…</p> : (
                    <div className="space-y-3">
                        <div className="flex flex-wrap gap-1.5">
                            {data.participants.map((p, i) => (
                                <button key={i} type="button" data-testid={`chat-participant-${i}`} onClick={() => p.user_id && setProfileId(p.user_id)}
                                    className="rounded bg-zinc-100 px-2 py-0.5 text-xs transition-colors hover:bg-accent/15" title={`${p.email || ""} — click to open profile`}>{p.temp_emoji} {p.temp_name} = <span className="font-600 underline-offset-2 hover:underline">{p.real_name || "—"}</span></button>
                            ))}
                        </div>
                        <Input placeholder="Search this chat…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="chat-monitor-search" className="h-8" />
                        <div className="max-h-[50vh] space-y-2 overflow-y-auto rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="chat-monitor-messages">
                            {msgs.length === 0 && <p className="text-sm text-zinc-400">No messages.</p>}
                            {msgs.map((m) => m.system ? (
                                <p key={m.id} className="text-center text-[11px] text-zinc-400">{m.temp_emoji} {m.text}</p>
                            ) : (
                                <div key={m.id} className={`group rounded p-1.5 text-sm ${m.flagged ? "bg-red-50" : ""}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <span className="font-600">{m.temp_emoji} {m.temp_name}</span>
                                            <span className="ml-1 text-xs text-zinc-400">({m.real_name || "unknown"})</span>
                                            <p className="text-zinc-700">{m.text}</p>
                                        </div>
                                        <div className="flex shrink-0 items-center gap-1.5">
                                            <button data-testid={`flag-${m.id}`} onClick={() => flag(m.id, !m.flagged)} title={m.flagged ? "Unflag" : "Flag"}
                                                className={`${m.flagged ? "text-red-600" : "text-zinc-300 hover:text-red-500"}`}>
                                                <Flag className="h-4 w-4" fill={m.flagged ? "currentColor" : "none"} />
                                            </button>
                                            {m.user_id && (
                                                <button data-testid={`chat-block-${m.id}`} onClick={() => blockUser(m.user_id, m.real_name)} title={`Block ${m.real_name || "member"}`}
                                                    className="text-zinc-300 hover:text-red-600"><UserX className="h-4 w-4" /></button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-[11px] text-zinc-400">Read-only · live (refreshes every 5s)</p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
        <MemberDialog userId={profileId} onClose={() => setProfileId(null)} />
        </>
    );
}
